import React, { useEffect, useMemo, useState } from 'react'
import { portal } from '../portalApi.js'

// 관리자 — 사용자/권한그룹/허용 이메일 (/api/auth/admin/*)
//
// 화면이 서른 개를 넘어가면서 사람 × 화면 체크박스 표로는 못 고르게 됐다. 그래서 역할을
// 그룹으로 만들어 사람에 붙이고, 그룹에 안 맞는 예외만 개별 권한으로 남긴다.
// 실제 허용 = 그룹 ∪ 개별. 어느 쪽에서 받은 권한인지 화면에서 구분해 보여준다.

function ScreenPicker({ screens, value, onChange, lockedBy }) {
  const menuOnly = useMemo(
    () => new Set(screens.filter((s) => s.menu !== false).map((s) => s.key)), [screens])
  const groups = useMemo(() => {
    const m = new Map()
    for (const s of screens) {
      if (!m.has(s.group)) m.set(s.group, [])
      m.get(s.group).push(s)
    }
    return [...m.entries()]
  }, [screens])

  const toggle = (k) =>
    onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k])
  // 분류 전체 토글은 메뉴 화면만 건드린다. 기능 권한(팀장 등)까지 딸려 켜지면
  // "업무관리 주세요" 하고 분류를 눌렀다가 전원이 팀장이 된다 — 실제로 그렇게 됐다.
  const bulk = (keys) => keys.filter((k) => !lockedBy?.[k] && menuOnly.has(k))
  const toggleAll = (keys) => {
    const free = bulk(keys)
    if (!free.length) return
    const all = free.every((k) => value.includes(k))
    onChange(all ? value.filter((k) => !free.includes(k)) : [...new Set([...value, ...free])])
  }

  return (
    <div className="perm-grid">
      {groups.map(([g, items]) => {
        const keys = items.map((s) => s.key)
        const on = keys.filter((k) => value.includes(k) || lockedBy?.[k]).length
        return (
          <div key={g} className="perm-cat">
            <div className="perm-cat-head">
              <button type="button" className="link-cell" onClick={() => toggleAll(keys)}
                title="이 묶음의 화면을 한 번에 켜고 끕니다 (기능 권한은 따로 켜야 합니다)">
                {g}
              </button>
              <span className="muted">{on}/{keys.length}</span>
            </div>
            {items.map((s) => {
              const locked = lockedBy?.[s.key]
              const label = s.label.includes(' · ') ? s.label.split(' · ')[1] : s.label
              return (
                <label key={s.key} className={'perm-item' + (locked ? ' locked' : '')}
                  title={locked ? `${locked} 그룹으로 허용된 화면입니다 — 끄려면 그 그룹에서 빼세요`
                    : s.menu === false ? `${s.label} — 메뉴가 따로 생기지 않고, 그 화면 안에서 할 수 있는 일이 늘어납니다`
                      : s.label}>
                  <input type="checkbox" disabled={!!locked}
                    checked={!!locked || value.includes(s.key)}
                    onChange={() => toggle(s.key)} />
                  <span>{label}</span>
                  {s.menu === false && <span className="perm-flag">기능</span>}
                  {locked && <span className="perm-from">{locked}</span>}
                </label>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

// 회사는 화면 권한과 다른 축이라 고르는 칸을 따로 둔다. 그룹과 사람이 같은 칸을 쓴다 -
// 두 벌로 만들면 한쪽만 고쳐져서 «그룹에선 됐는데 사람에선 안 된다»가 생긴다.
function CompanyPicker({ 회사목록, value, onChange, locked, disabled }) {
  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: 6, margin: '0 0 14px' }}>
      {회사목록.map((c) => {
        const 그룹에서 = (locked || {})[c.code]
        const 켜짐 = value.includes(c.code) || !!그룹에서
        return (
          <button key={c.code} type="button" disabled={disabled || !!그룹에서}
            className={'data-tab' + (켜짐 ? ' active' : '')}
            title={그룹에서 ? `${그룹에서} 그룹에서 열린 회사입니다 — 그 그룹에서 빼야 합니다` : c.type}
            onClick={() => onChange(value.includes(c.code)
              ? value.filter((x) => x !== c.code) : [...value, c.code])}>
            {c.name} <span className="muted">{그룹에서 || c.type}</span>
          </button>
        )
      })}
      {!회사목록.length && <span className="muted">회사 목록을 못 불러왔습니다</span>}
    </div>
  )
}

function GroupModal({ screens, 회사목록, group, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || '')
  const [note, setNote] = useState(group?.note || '')
  const [picked, setPicked] = useState(group?.screens || [])
  const [cos, setCos] = useState(group?.companies || [])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const save = async () => {
    setBusy(true); setErr('')
    try {
      await portal.post('/api/auth/admin/groups',
        { id: group?.id, name, note, screens: picked, companies: cos })
      onSaved(); onClose()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 780 }}>
        <div className="modal-header">
          <h3>{group?.id ? '권한그룹 수정' : '권한그룹 만들기'}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        {/* 내용이 화면보다 길어지면 패널이 잘려 「저장」에 닿을 수가 없었다. 구르는 자리를
            안쪽에 주고 단추는 그 밖에 둔다(styles.css 의 modal-body/modal-actions). */}
        <div className="modal-body">
          <div className="row" style={{ marginBottom: 10, gap: 8 }}>
            <input placeholder="그룹 이름 (예: 자금담당)" value={name} style={{ width: 200 }}
              onChange={(e) => setName(e.target.value)} />
            <input placeholder="설명 (선택)" value={note} style={{ flex: 1 }}
              onChange={(e) => setNote(e.target.value)} />
          </div>
          <b>볼 수 있는 회사</b>
          <div className="hint" style={{ margin: '4px 0 8px' }}>
            이 그룹에 든 사람은 여기 고른 회사의 장부를 봅니다. 화면 권한과 <b>다른 축</b>이라
            자금일보 권한이 있어도 회사가 안 열려 있으면 본사 것만 보입니다.
            아무것도 안 고르면 회사는 이 그룹으로 열리지 않습니다(사람별 지정은 그대로 삽니다).
          </div>
          <CompanyPicker 회사목록={회사목록} value={cos} onChange={setCos} />

          <b>허용 화면</b>
          <div className="hint" style={{ margin: '4px 0 8px' }}>
            분류 이름을 누르면 그 묶음 전체가 켜지고 꺼집니다. 선택 {picked.length}개.
          </div>
          <ScreenPicker screens={screens} value={picked} onChange={setPicked} />
          {err && <div className="error-box" style={{ marginTop: 10 }}>{err}</div>}
        </div>
        <div className="modal-actions">
          <span className="muted" style={{ fontSize: 12, marginRight: 'auto' }}>
            {group?.userCount ? `${group.userCount}명이 이 그룹을 쓰고 있습니다 — 저장하면 즉시 반영됩니다` : ''}
          </span>
          <button type="button" className="btn secondary" onClick={onClose}>취소</button>
          <button type="button" className="btn" disabled={busy || !name.trim()} onClick={save}>
            {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  )
}

function UserPermModal({ screens, groups, 회사목록, user, onClose, onSaved }) {
  const [gids, setGids] = useState(user.groupIds || [])
  const [direct, setDirect] = useState(user.directScreens || [])
  const [cos, setCos] = useState(user.companyCodes || [])
  // 그룹으로 이미 열린 회사는 개인에게 또 줄 필요가 없다 — 어디서 왔는지 보여 준다
  const 회사잠금 = useMemo(() => {
    const m = {}
    for (const g of groups) {
      if (!gids.includes(g.id)) continue
      for (const c of (g.companies || [])) if (!m[c]) m[c] = g.name
    }
    return m
  }, [groups, gids])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  // 그룹으로 이미 받는 화면은 개별로 또 켤 필요가 없다 — 어디서 온 권한인지 표시해 준다
  const lockedBy = useMemo(() => {
    const m = {}
    for (const g of groups) {
      if (!gids.includes(g.id)) continue
      for (const k of g.screens) if (!m[k]) m[k] = g.name
    }
    return m
  }, [groups, gids])

  const effective = useMemo(
    () => new Set([...Object.keys(lockedBy), ...direct]), [lockedBy, direct])

  // 그룹이 이미 여는 화면을 개별로도 갖고 있으면 남겨 둘 이유가 없다. 그대로 두면 나중에
  // 그룹에서 화면을 빼도 이 사람만 계속 열려 있어, 그룹으로 관리하는 의미가 사라진다.
  const redundant = useMemo(() => direct.filter((k) => lockedBy[k]), [direct, lockedBy])

  const save = async () => {
    setBusy(true); setErr('')
    try {
      await portal.post(`/api/auth/admin/users/${user.id}/groups`, { groups: gids })
      await portal.post(`/api/auth/admin/users/${user.id}/permissions`, { screens: direct })
      await portal.post(`/api/auth/admin/users/${user.id}/companies`, { companies: cos })
      onSaved(); onClose()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 780 }}>
        <div className="modal-header">
          <h3>{user.name} — 권한</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <b>권한그룹</b>
          <div className="row" style={{ flexWrap: 'wrap', gap: 6, margin: '6px 0 14px' }}>
            {groups.map((g) => (
              <button key={g.id} type="button"
                className={'data-tab' + (gids.includes(g.id) ? ' active' : '')}
                title={g.note || `화면 ${g.screens.length}개`}
                onClick={() => setGids(gids.includes(g.id)
                  ? gids.filter((x) => x !== g.id) : [...gids, g.id])}>
                {g.name} <span className="muted">{g.screens.length}</span>
              </button>
            ))}
            {!groups.length && <span className="muted">아직 만든 그룹이 없습니다</span>}
          </div>

          <b>볼 수 있는 회사</b>
          <div className="hint" style={{ margin: '4px 0 8px' }}>
            화면 권한과 <b>다른 축</b>입니다 — 자금일보를 볼 수 있어도 회사가 안 열려 있으면
            본사 것만 보입니다. 그룹에서 온 회사는 잠겨 있고(그 그룹에서 빼야 합니다),
            여기서 켜는 것은 그룹에 안 맞는 예외입니다. 그룹·개인 어느 쪽도 없으면
            <b>본사만</b> 보입니다.
            {user.isAdmin && ' 이 사람은 전권자라 어차피 전부 봅니다.'}
          </div>
          <CompanyPicker 회사목록={회사목록} value={cos} onChange={setCos}
            locked={회사잠금} disabled={user.isAdmin} />

          <b>허용 화면</b>
          <div className="hint" style={{ margin: '4px 0 8px' }}>
            체크된 것이 이 사람이 볼 수 있는 화면입니다. 회색으로 잠긴 것은 그룹에서 온 것이라
            여기서 끌 수 없고, 그 그룹에서 빼야 합니다. 나머지는 그룹에 안 맞는 예외로 직접 켜는
            개별 권한입니다. 실제 허용 <b>{effective.size}</b>개 (그룹 {Object.keys(lockedBy).length}
            {' '}· 개별 {direct.filter((k) => !lockedBy[k]).length}).
          </div>
          {redundant.length > 0 && (
            <div className="hint" style={{ margin: '0 0 8px', borderColor: 'var(--warn)' }}>
              개별 권한 {redundant.length}개가 그룹과 겹칩니다. 지워도 허용 화면은 그대로이고,
              앞으로는 그룹만 고치면 됩니다.{' '}
              <button type="button" className="link-cell"
                onClick={() => setDirect(direct.filter((k) => !lockedBy[k]))}>
                겹치는 개별 권한 지우기
              </button>
            </div>
          )}
          <ScreenPicker screens={screens} value={direct} onChange={setDirect} lockedBy={lockedBy} />

          {err && <div className="error-box" style={{ marginTop: 10 }}>{err}</div>}
        </div>
        <div className="modal-actions">
          <span className="muted" style={{ fontSize: 12, marginRight: 'auto' }}>
            고친 것은 <b>저장</b>을 눌러야 들어갑니다 — 창을 그냥 닫으면 버려집니다.
          </span>
          <button type="button" className="btn secondary" onClick={onClose}>취소</button>
          <button type="button" className="btn" disabled={busy} onClick={save}>
            {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Admin() {
  const [screens, setScreens] = useState([])
  const [users, setUsers] = useState(null)
  const [groups, setGroups] = useState([])
  const [emails, setEmails] = useState([])
  const [newUser, setNewUser] = useState({ name: '', username: '' })
  const [newEmail, setNewEmail] = useState('')
  const [created, setCreated] = useState(null)
  const [err, setErr] = useState('')
  const [toast, setToast] = useState('')
  const [editGroup, setEditGroup] = useState(null)
  const [editUser, setEditUser] = useState(null)
  const [회사목록, set회사목록] = useState([])

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2500) }

  const load = () => {
    setErr('')
    Promise.all([
      portal.get('/api/auth/admin/screens'),
      portal.get('/api/auth/admin/users'),
      portal.get('/api/auth/admin/groups'),
      portal.get('/api/auth/admin/allowed-emails'),
      portal.get('/api/auth/admin/companies'),
    ]).then(([s, u, g, e, co]) => {
      setScreens(s); setUsers(u); setGroups(g); setEmails(e); set회사목록(co)
    })
      .catch((e) => setErr(e.status === 403 ? '관리자만 접근할 수 있습니다.' : e.message))
  }
  useEffect(() => { load() }, [])

  const groupName = useMemo(
    () => Object.fromEntries(groups.map((g) => [g.id, g.name])), [groups])

  const removeGroup = async (g) => {
    if (!confirm(`'${g.name}' 그룹을 지울까요?`
      + (g.userCount ? `\n${g.userCount}명이 이 그룹으로 받던 권한이 사라집니다.` : ''))) return
    try { await portal.del(`/api/auth/admin/groups/${g.id}`); load(); notify('삭제했습니다') }
    catch (e) { setErr(e.message) }
  }

  // 이미 개별로 붙어 있는 권한을 그대로 그룹으로 옮기는 통로. 없으면 서른 개를 다시 찍어야 한다.
  const groupFromUser = (u) => setEditGroup({
    name: '', note: `${u.name}의 현재 권한으로 만듦`, screens: [...u.screens],
  })

  const rename = async (u) => {
    const name = prompt('새 이름', u.name)
    if (!name || name === u.name) return
    try { await portal.post(`/api/auth/admin/users/${u.id}/name`, { name }); load() }
    catch (e) { setErr(e.message) }
  }

  const removeUser = async (u) => {
    if (!confirm(`${u.name} 사용자를 삭제할까요?`)) return
    try { await portal.del(`/api/auth/admin/users/${u.id}`); load() }
    catch (e) { setErr(e.message) }
  }

  const createUser = async () => {
    try {
      const r = await portal.post('/api/auth/admin/users/local', newUser)
      setCreated(r); setNewUser({ name: '', username: '' }); load()
    } catch (e) { setErr(e.message) }
  }

  const addEmail = async () => {
    try {
      await portal.post('/api/auth/admin/allowed-emails', { email: newEmail })
      setNewEmail(''); notify('추가 완료'); load()
    } catch (e) { setErr(e.message) }
  }

  const removeEmail = async (em) => {
    try { await portal.del(`/api/auth/admin/allowed-emails/${encodeURIComponent(em.email ?? em)}`); load() }
    catch (e) { setErr(e.message) }
  }

  if (err && !users) return <><h2>관리자</h2><div className="error-box">{err}</div></>
  if (!users) return <div className="muted">불러오는 중…</div>

  return (
    <>
      <h2>관리자 — 사용자 및 권한</h2>
      {err && <div className="error-box">{err}</div>}

      <div className="panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>권한그룹</h3>
          <button type="button" className="btn" onClick={() => setEditGroup({})}>그룹 만들기</button>
        </div>
        <div className="hint" style={{ margin: '8px 0' }}>
          역할을 그룹으로 만들어 사람에 붙입니다. 그룹 내용을 고치면 그 그룹을 쓰는 사람 전원에게
          바로 반영됩니다. 실제 허용은 <b>그룹 + 개별 권한</b>을 합친 것입니다.
        </div>
        <table>
          <thead>
            <tr><th className="l">그룹</th><th className="l">설명</th><th>화면</th>
              <th className="l">회사</th><th>인원</th><th /></tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.id}>
                <td className="l">
                  <button type="button" className="link-cell" onClick={() => setEditGroup(g)}>{g.name}</button>
                </td>
                <td className="l muted">{g.note || ''}</td>
                <td className="c">{g.screens.length}</td>
                <td className="l" style={{ fontSize: 12 }}>
                  {(g.companies || []).length
                    ? g.companies.map((c) => (
                      <span key={c} className="perm-chip">
                        {(회사목록.find((x) => x.code === c) || {}).name || c}
                      </span>))
                    : <span className="muted">—</span>}
                </td>
                <td className="c">{g.userCount}</td>
                <td className="c" style={{ width: 60 }}>
                  <button type="button" className="btn danger" onClick={() => removeGroup(g)}>삭제</button>
                </td>
              </tr>
            ))}
            {!groups.length && (
              <tr><td colSpan={6} className="c muted">
                아직 그룹이 없습니다 — 아래 사용자 줄의 "현재 권한으로 그룹 만들기"를 쓰면
                지금 붙어 있는 권한을 그대로 옮길 수 있습니다
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h3>사용자</h3>
        <table>
          <thead>
            <tr>
              <th className="l">이름</th><th className="l">계정</th><th>유형</th>
              <th className="l">권한그룹</th><th>개별</th><th>허용 화면</th>
              <th className="l">볼 수 있는 회사</th><th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td className="l">{u.name}{u.isAdmin && <span className="muted"> · 관리자</span>}</td>
                <td className="l">{u.email || u.username}</td>
                <td className="c">{u.auth_type ?? u.authType}</td>
                <td className="l">
                  {u.isAdmin ? <span className="muted">전체 (관리자)</span>
                    : (u.groupIds || []).length
                      ? (u.groupIds || []).map((id) => (
                        <span key={id} className="perm-chip">{groupName[id] || id}</span>))
                      : <span className="muted">—</span>}
                </td>
                <td className="c">{u.isAdmin ? '—' : (u.directScreens || []).length || ''}</td>
                <td className="c">{u.isAdmin ? screens.length : u.screens.length}</td>
                <td className="l" style={{ fontSize: 12 }}>
                  {u.isAdmin ? <span className="muted">전체 (관리자)</span>
                    : (() => {
                        const 합 = [...new Set([...(u.groupCompanies || []), ...(u.companyCodes || [])])]
                        if (!합.length) return <span className="muted">본사만</span>
                        return 합.map((c) => (
                          <span key={c} className="perm-chip"
                            title={(u.groupCompanies || []).includes(c) ? '권한그룹에서' : '개별 지정'}>
                            {(회사목록.find((x) => x.code === c) || {}).name || c}
                          </span>))
                      })()}
                </td>
                <td className="c" style={{ whiteSpace: 'nowrap' }}>
                  {!u.isAdmin && (
                    <button type="button" className="btn secondary" style={{ padding: '3px 8px', fontSize: 11 }}
                      onClick={() => setEditUser(u)}>권한</button>
                  )}{' '}
                  {!u.isAdmin && u.screens.length > 0 && (
                    <button type="button" className="btn secondary" style={{ padding: '3px 8px', fontSize: 11 }}
                      title="지금 이 사람에게 허용된 화면으로 새 그룹을 만듭니다"
                      onClick={() => groupFromUser(u)}>그룹으로</button>
                  )}{' '}
                  <button type="button" className="btn secondary" style={{ padding: '3px 8px', fontSize: 11 }}
                    onClick={() => rename(u)}>이름</button>{' '}
                  {!u.isAdmin && <button type="button" className="btn danger" onClick={() => removeUser(u)}>삭제</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h3>로컬 계정 만들기</h3>
        <div className="row">
          <input placeholder="이름*" value={newUser.name}
            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
          <input placeholder="아이디(비우면 자동)" value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
          <button type="button" className="btn" onClick={createUser} disabled={!newUser.name.trim()}>생성</button>
        </div>
        {created && (
          <div className="hint" style={{ marginTop: 8 }}>
            생성됨 — 아이디 <b>{created.username}</b> / 임시 비밀번호{' '}
            <b>{created.tempPassword ?? created.temp_password ?? '(응답 확인)'}</b> (첫 로그인 시 변경)
          </div>
        )}
      </div>

      <div className="panel">
        <h3>구글 로그인 허용 이메일</h3>
        <div className="row" style={{ marginBottom: 10 }}>
          <input placeholder="name@ermore.co.kr" style={{ width: 240 }} value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addEmail()} />
          <button type="button" className="btn" onClick={addEmail} disabled={!newEmail.includes('@')}>추가</button>
          <span className="muted">@ermore.co.kr 도메인은 기본 허용 · 외부 이메일만 등록</span>
        </div>
        <table style={{ maxWidth: 560 }}>
          <tbody>
            {emails.length === 0 && <tr><td className="c muted">등록된 외부 이메일이 없습니다</td></tr>}
            {emails.map((em) => {
              const addr = em.email ?? em
              return (
                <tr key={addr}>
                  <td className="l">{addr}</td>
                  <td className="c" style={{ width: 70 }}>
                    <button type="button" className="btn danger" onClick={() => removeEmail(em)}>삭제</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editGroup && (
        <GroupModal screens={screens} 회사목록={회사목록} group={editGroup}
          onClose={() => setEditGroup(null)} onSaved={load} />
      )}
      {editUser && (
        <UserPermModal screens={screens} groups={groups} 회사목록={회사목록} user={editUser}
          onClose={() => setEditUser(null)} onSaved={load} />
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
